// apps/web/src/pages/settings/availability.tsx — Phase 03 plan 03-03.
//
// Admin surface for the four availability models wired in plan 03-02:
//   1. SlotDefinition (CRUD: create / update / delete)
//   2. CapacityConfig (upsert; 4 scopes: Standard / Date / Slot / Date+Slot)
//   3. BlockedPeriod (create / delete; range validation)
//   4. Booking rules + confirmationMode (pair: updateRestaurant +
//      updateConfirmationMode, per D-16)
//
// Lives inside the existing AdminShell wrapper (no second chrome).
// Uses the existing TanStack-Query cache-key pattern (`["availability-admin",
// "<entity>"]`) and invalidates `["availability"]`, `["slots"]`, and the
// admin prefix on every successful mutation per Phase 03 Patterns §6.
//
// Destructive deletes use the inline two-step pattern from 03-UI-SPEC
// §Admin Copy: first click arms the destructive confirmation, second
// click fires the mutation. No window.confirm, no native <dialog>.

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Trash2 } from "lucide-react";
import { gqlRequest } from "../../lib/api";
import {
  BLOCKED_PERIODS_QUERY,
  CAPACITY_CONFIGS_QUERY,
  CREATE_BLOCKED_PERIOD_MUTATION,
  CREATE_SLOT_DEFINITION_MUTATION,
  DELETE_BLOCKED_PERIOD_MUTATION,
  DELETE_SLOT_DEFINITION_MUTATION,
  RESTAURANT_QUERY,
  SLOT_DEFINITIONS_QUERY,
  UPDATE_CONFIRMATION_MODE_MUTATION,
  UPDATE_RESTAURANT_MUTATION,
  UPDATE_SLOT_DEFINITION_MUTATION,
  UPSERT_CAPACITY_CONFIG_MUTATION,
} from "./operations";

type Weekday1to7 = "ONE" | "TWO" | "THREE" | "FOUR" | "FIVE" | "SIX" | "SEVEN";
type ConfirmationMode = "AUTO" | "MANUAL";
type CapacityScope = "STANDARD" | "DATE" | "SLOT" | "DATE_SLOT";

// Explicit lookup: enum → i18n key. The previous `${s.charAt(0)}${s.slice(1).toLowerCase().replace("_", "")}`
// reconstruction produced `scopeDateslot` for `DATE_SLOT`, which does not exist in
// either bundle — clicking the 4th scope button showed a raw key / empty string.
// Adding a new scope requires editing both this map AND the literal list in
// availability.test.tsx; that intentional friction is what the previous
// implementation tried to avoid and what shipped the bug.
const SCOPE_LABEL_KEY: Record<CapacityScope, string> = {
  STANDARD: "availability.admin.capacity.scopeStandard",
  DATE: "availability.admin.capacity.scopeDate",
  SLOT: "availability.admin.capacity.scopeSlot",
  DATE_SLOT: "availability.admin.capacity.scopeDateSlot",
};

interface SlotDefinition {
  id: string;
  startTime: string;
  durationMinutes: number;
  active: boolean;
  capacityOverride: number | null;
  holdbackOverride: number | null;
  weekdayBinding: Weekday1to7[];
}

interface CapacityConfig {
  id: string;
  date: string | null;
  slotDefinitionId: string | null;
  onlineHoldback: number;
  telefonHoldback: number;
  vorOrtHoldback: number;
  capacityOverride: number | null;
  slotCapacityOverride: number | null;
}

interface BlockedPeriod {
  id: string;
  fromDate: string;
  toDate: string;
  fromTime: string | null;
  toTime: string | null;
  reason: string | null;
}

interface RestaurantPolicy {
  id: string;
  capacityDefault: number;
  holdbackDefault: number;
  minPartySize: number;
  maxPartySize: number;
  maxAdvanceDays: number;
  minAdvanceMinutes: number;
  sameDayCutoffTime: string;
  confirmationMode: ConfirmationMode;
}

const WEEKDAY_KEYS: { value: Weekday1to7; i18nKey: string }[] = [
  { value: "ONE", i18nKey: "weekdayMo" },
  { value: "TWO", i18nKey: "weekdayTu" },
  { value: "THREE", i18nKey: "weekdayWe" },
  { value: "FOUR", i18nKey: "weekdayTh" },
  { value: "FIVE", i18nKey: "weekdayFr" },
  { value: "SIX", i18nKey: "weekdaySa" },
  { value: "SEVEN", i18nKey: "weekdaySo" },
];

// Phase 4 follow-up #3: per-channel holdback cap. Must stay in sync
// with `HOLDBAX_MAX_PER_CHANNEL` in
// apps/api/src/graphql/resolvers/availability-admin.ts:235 — the
// server is still the source of truth; this client-side clamp is
// belt-and-braces so React state can never carry a value the server
// will reject. NaN / undefined input falls back to 0 (the previous
// behaviour of the inline `|| 0`).
const HOLDBACK_MAX_PER_CHANNEL = 10;
function clampHoldback(raw: number): number {
  if (!Number.isFinite(raw) || raw < 0) return 0;
  if (raw > HOLDBACK_MAX_PER_CHANNEL) return HOLDBACK_MAX_PER_CHANNEL;
  return Math.trunc(raw);
}

// Invalidate every cache prefix that may have cached data dependent on
// the entity just mutated. Per Phase 03 Patterns §6 the mutations share
// three prefix namespaces: `availability` (public), `slots` (public),
// and `availability-admin` (this admin page).
function invalidateAvailabilityCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  adminSuffix: string,
): void {
  void queryClient.invalidateQueries({ queryKey: ["availability"] });
  void queryClient.invalidateQueries({ queryKey: ["slots"] });
  void queryClient.invalidateQueries({
    queryKey: ["availability-admin", adminSuffix],
  });
}

export function AvailabilityPage() {
  const { t } = useTranslation();

  return (
    <main
      className="min-h-screen bg-bg text-text font-body"
      data-testid="availability-page"
    >
      <header className="sticky top-0 z-10 bg-bg border-b border-divider px-4 py-3 flex items-center gap-3">
        <Link
          to="/admin/einstellungen"
          className="text-text/70 hover:text-text"
          aria-label={t("settings.hub.backToHub")}
          data-testid="availability-back"
        >
          ‹
        </Link>
        <h1
          className="font-heading text-2xl md:text-3xl"
          data-testid="availability-title"
        >
          {t("availability.admin.title")}
        </h1>
      </header>

      <div className="px-4 py-6 w-full max-w-3xl space-y-8">
        <SlotsSection />
        <CapacitySection />
        <BlockedPeriodsSection />
        <BookingRulesSection />
      </div>
    </main>
  );
}

// ---------------- Section 1: SlotDefinition ----------------

function SlotsSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const slotsQuery = useQuery<{ slotDefinitions: SlotDefinition[] }>({
    queryKey: ["availability-admin", "slotDefinitions"],
    queryFn: async () =>
      gqlRequest<{ slotDefinitions: SlotDefinition[] }>(SLOT_DEFINITIONS_QUERY),
  });

  const [editor, setEditor] = useState<{
    mode: "create" | "edit";
    id: string | null;
    open: boolean;
  }>({ mode: "create", id: null, open: false });
  const [armedDeleteId, setArmedDeleteId] = useState<string | null>(null);

  const slots = useMemo(
    () =>
      (slotsQuery.data?.slotDefinitions ?? [])
        .slice()
        .sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [slotsQuery.data],
  );

  const createMutation = useMutation({
    mutationFn: async (input: {
      startTime: string;
      durationMinutes: number;
      active: boolean;
      capacityOverride: number | null;
      holdbackOverride: number | null;
      weekdayBinding: Weekday1to7[];
    }) =>
      gqlRequest<{ createSlotDefinition: SlotDefinition }>(
        CREATE_SLOT_DEFINITION_MUTATION,
        { input },
      ),
    onSuccess: () => {
      invalidateAvailabilityCaches(queryClient, "slotDefinitions");
      setEditor({ mode: "create", id: null, open: false });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (vars: {
      id: string;
      input: {
        startTime: string;
        durationMinutes: number;
        active: boolean;
        capacityOverride: number | null;
        holdbackOverride: number | null;
        weekdayBinding: Weekday1to7[];
      };
    }) =>
      gqlRequest<{ updateSlotDefinition: SlotDefinition }>(
        UPDATE_SLOT_DEFINITION_MUTATION,
        vars,
      ),
    onSuccess: () => {
      invalidateAvailabilityCaches(queryClient, "slotDefinitions");
      setEditor({ mode: "create", id: null, open: false });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) =>
      gqlRequest<{ deleteSlotDefinition: boolean }>(
        DELETE_SLOT_DEFINITION_MUTATION,
        { id },
      ),
    onSuccess: () => {
      invalidateAvailabilityCaches(queryClient, "slotDefinitions");
      setArmedDeleteId(null);
    },
  });

  const editing = editor.open
    ? (slots.find((s) => s.id === editor.id) ?? null)
    : null;

  return (
    <section data-testid="section-slots">
      <h2 className="font-heading text-xl mb-2">
        {t("availability.admin.sectionSlots")}
      </h2>

      <div className="flex justify-end mb-3">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() =>
            setEditor({ mode: "create", id: null, open: !editor.open })
          }
          data-testid="slot-add"
        >
          {t("availability.admin.slot.add")}
        </button>
      </div>

      {editor.open && (
        <SlotEditor
          mode={editor.mode}
          existing={editing}
          pending={
            createMutation.isPending ||
            updateMutation.isPending ||
            deleteMutation.isPending
          }
          onCancel={() => setEditor({ mode: "create", id: null, open: false })}
          onSubmit={(input) => {
            if (editor.mode === "edit" && editor.id) {
              updateMutation.mutate({ id: editor.id, input });
            } else {
              createMutation.mutate(input);
            }
          }}
        />
      )}

      {slotsQuery.isLoading && (
        <p className="text-sm text-text/70" data-testid="slot-loading">
          …
        </p>
      )}

      {slotsQuery.data && slots.length === 0 && (
        <p className="text-sm text-text/70" data-testid="slot-empty">
          {t("availability.admin.slot.empty")}
        </p>
      )}

      {slots.length > 0 && (
        <ul className="card divide-y divide-divider" data-testid="slot-list">
          {slots.map((s) => (
            <SlotRow
              key={s.id}
              slot={s}
              armedDelete={armedDeleteId === s.id}
              onArmDelete={() => setArmedDeleteId(s.id)}
              onCancelDelete={() => setArmedDeleteId(null)}
              onConfirmDelete={() => deleteMutation.mutate(s.id)}
              onEdit={() => setEditor({ mode: "edit", id: s.id, open: true })}
            />
          ))}
        </ul>
      )}

      {(createMutation.isError || updateMutation.isError) && (
        <p
          className="text-sm text-red-700 mt-3"
          role="alert"
          data-testid="slot-error"
        >
          {t("availability.actions.saveFailure")}
        </p>
      )}
    </section>
  );
}

function SlotRow({
  slot,
  armedDelete,
  onArmDelete,
  onCancelDelete,
  onConfirmDelete,
  onEdit,
}: {
  slot: SlotDefinition;
  armedDelete: boolean;
  onArmDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
  onEdit: () => void;
}) {
  const { t } = useTranslation();
  const activeDays = WEEKDAY_KEYS.filter((w) =>
    slot.weekdayBinding.includes(w.value),
  )
    .map((w) => t(`availability.admin.slot.${w.i18nKey}`))
    .join(" ");

  return (
    <li
      className="px-4 py-3 flex flex-wrap items-center gap-3"
      data-testid={`slot-row-${slot.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="font-medium">
          {slot.startTime} · {slot.durationMinutes} Min.
        </div>
        <div className="text-sm text-text/70">
          {activeDays || "—"}
          {slot.capacityOverride !== null && (
            <span className="ml-2">
              · {slot.capacityOverride}{" "}
              {t("availability.admin.capacity.columnTotal")}
            </span>
          )}
          {slot.holdbackOverride !== null && (
            <span className="ml-2">
              · {slot.holdbackOverride}{" "}
              {t("availability.admin.capacity.columnOnline")}
            </span>
          )}
        </div>
        <div className="text-sm text-text/70">
          {slot.active ? (
            <span className="tag" data-testid={`slot-row-${slot.id}-active`}>
              {t("availability.admin.slot.active")}
            </span>
          ) : (
            <span className="tag" data-testid={`slot-row-${slot.id}-inactive`}>
              {t("availability.admin.slot.active")}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3">
        {!armedDelete && (
          <>
            <button
              type="button"
              className="p-1 text-text/70 hover:text-text"
              onClick={onEdit}
              aria-label={t("availability.admin.slot.edit")}
              data-testid={`slot-row-${slot.id}-edit`}
            >
              <Pencil className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              className="p-1 text-text/70 hover:text-red-700"
              onClick={onArmDelete}
              aria-label={t("availability.admin.slot.delete")}
              data-testid={`slot-row-${slot.id}-delete`}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </button>
          </>
        )}
        {armedDelete && (
          <div
            className="flex flex-wrap items-center gap-2"
            role="group"
            data-testid={`slot-row-${slot.id}-armed`}
          >
            <span className="text-sm">
              {t("availability.admin.slot.deleteArmed", {
                time: slot.startTime,
              })}
            </span>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancelDelete}
              data-testid={`slot-row-${slot.id}-cancel-delete`}
            >
              {t("availability.admin.slot.cancelDelete")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onConfirmDelete}
              data-testid={`slot-row-${slot.id}-confirm-delete`}
            >
              {t("availability.admin.slot.deleteConfirm")}
            </button>
          </div>
        )}
      </div>
    </li>
  );
}

function SlotEditor({
  mode,
  existing,
  pending,
  onCancel,
  onSubmit,
}: {
  mode: "create" | "edit";
  existing: SlotDefinition | null;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (input: {
    startTime: string;
    durationMinutes: number;
    active: boolean;
    capacityOverride: number | null;
    holdbackOverride: number | null;
    weekdayBinding: Weekday1to7[];
  }) => void;
}) {
  const { t } = useTranslation();
  const [startTime, setStartTime] = useState(existing?.startTime ?? "12:00");
  const [durationMinutes, setDurationMinutes] = useState(
    existing?.durationMinutes ?? 30,
  );
  const [active, setActive] = useState(existing?.active ?? true);
  const [weekdayBinding, setWeekdayBinding] = useState<Weekday1to7[]>(
    existing?.weekdayBinding ?? ["ONE", "TWO", "THREE", "FOUR", "FIVE", "SIX"],
  );
  const [capacityOverride, setCapacityOverride] = useState<string>(
    existing?.capacityOverride !== null &&
      existing?.capacityOverride !== undefined
      ? String(existing.capacityOverride)
      : "",
  );
  const [holdbackOverride, setHoldbackOverride] = useState<string>(
    existing?.holdbackOverride !== null &&
      existing?.holdbackOverride !== undefined
      ? String(existing.holdbackOverride)
      : "",
  );

  function toggleWeekday(w: Weekday1to7) {
    setWeekdayBinding((prev) =>
      prev.includes(w) ? prev.filter((x) => x !== w) : [...prev, w],
    );
  }

  function submit() {
    const capacityParsed =
      capacityOverride.trim().length === 0
        ? null
        : Number.parseInt(capacityOverride, 10);
    const holdbackParsed =
      holdbackOverride.trim().length === 0
        ? null
        : Number.parseInt(holdbackOverride, 10);
    onSubmit({
      startTime,
      durationMinutes,
      active,
      capacityOverride:
        capacityParsed !== null && Number.isFinite(capacityParsed)
          ? capacityParsed
          : null,
      holdbackOverride:
        holdbackParsed !== null && Number.isFinite(holdbackParsed)
          ? holdbackParsed
          : null,
      weekdayBinding,
    });
  }

  return (
    <div
      className="card p-4 mb-4 space-y-3"
      data-testid={`slot-editor-${mode}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block">
          <span className="block text-sm font-medium text-text/70 mb-1">
            {t("availability.admin.slot.startTime")}
          </span>
          <input
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            className="field w-full"
            data-testid="slot-editor-start-time"
          />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-text/70 mb-1">
            {t("availability.admin.slot.duration")}
          </span>
          <input
            type="number"
            min={5}
            max={360}
            value={durationMinutes}
            onChange={(e) =>
              setDurationMinutes(Number.parseInt(e.target.value, 10) || 0)
            }
            className="field w-full"
            data-testid="slot-editor-duration"
          />
        </label>
      </div>

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          role="switch"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
          data-testid="slot-editor-active"
        />
        <span>{t("availability.admin.slot.active")}</span>
      </label>

      <div>
        <span className="block text-sm font-medium text-text/70 mb-1">
          {t("availability.admin.slot.weekdayBinding")}
        </span>
        <div className="flex flex-wrap gap-3">
          {WEEKDAY_KEYS.map((w) => (
            <label key={w.value} className="flex items-center gap-1 text-sm">
              <input
                type="checkbox"
                checked={weekdayBinding.includes(w.value)}
                onChange={() => toggleWeekday(w.value)}
                data-testid={`slot-editor-wd-${w.value}`}
              />
              <span>{t(`availability.admin.slot.${w.i18nKey}`)}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block">
          <span className="block text-sm font-medium text-text/70 mb-1">
            {t("availability.admin.slot.capacityOverride")}
          </span>
          <input
            type="number"
            min={0}
            value={capacityOverride}
            onChange={(e) => setCapacityOverride(e.target.value)}
            className="field w-full"
            data-testid="slot-editor-capacity"
          />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-text/70 mb-1">
            {t("availability.admin.slot.holdbackOverride")}
          </span>
          <input
            type="number"
            min={0}
            value={holdbackOverride}
            onChange={(e) => setHoldbackOverride(e.target.value)}
            className="field w-full"
            data-testid="slot-editor-holdback"
          />
        </label>
      </div>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={pending}
          data-testid="slot-editor-cancel"
        >
          {t("availability.admin.slot.cancel")}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={submit}
          disabled={pending || weekdayBinding.length === 0}
          data-testid="slot-editor-save"
        >
          {t("availability.admin.slot.save")}
        </button>
      </div>
    </div>
  );
}

// ---------------- Section 2: CapacityConfig ----------------

function CapacitySection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const configQuery = useQuery<{ capacityConfigs: CapacityConfig[] }>({
    queryKey: ["availability-admin", "capacityConfigs"],
    queryFn: async () =>
      gqlRequest<{ capacityConfigs: CapacityConfig[] }>(CAPACITY_CONFIGS_QUERY),
  });

  const slotsQuery = useQuery<{ slotDefinitions: SlotDefinition[] }>({
    queryKey: ["availability-admin", "slotDefinitions"],
    queryFn: async () =>
      gqlRequest<{ slotDefinitions: SlotDefinition[] }>(SLOT_DEFINITIONS_QUERY),
  });

  const restaurantQuery = useQuery<{ restaurant: RestaurantPolicy }>({
    queryKey: ["restaurant", "policy"],
    queryFn: async () =>
      gqlRequest<{ restaurant: RestaurantPolicy }>(RESTAURANT_QUERY),
  });

  const [scope, setScope] = useState<CapacityScope>("STANDARD");
  const [date, setDate] = useState("");
  const [slotId, setSlotId] = useState("");
  // Form state for the selected `current` row. Held as strings so the
  // inputs can carry an empty value (= clear override) without coercing
  // to 0; parsed to number|null in handleSave. Initialised to "" / 0
  // and re-synced via the effect below once `current` resolves from the
  // query — the prior version initialised everything to 0 once on mount
  // and never picked up the existing CapacityConfig, which left every
  // field blank even when a baseline row already existed.
  const [capacityOverride, setCapacityOverride] = useState("");
  const [onlineHoldback, setOnlineHoldback] = useState(0);
  const [telefonHoldback, setTelefonHoldback] = useState(0);
  const [vorOrtHoldback, setVorOrtHoldback] = useState(0);

  const success = restaurantQuery.data?.restaurant;
  const baseline = configQuery.data?.capacityConfigs.find(
    (c) => c.date === null && c.slotDefinitionId === null,
  );
  const dateRow = configQuery.data?.capacityConfigs.find(
    (c) => c.date === date && c.slotDefinitionId === null,
  );
  const slotRow = configQuery.data?.capacityConfigs.find(
    (c) => c.date === null && c.slotDefinitionId === slotId,
  );
  const dateSlotRow = configQuery.data?.capacityConfigs.find(
    (c) => c.date === date && c.slotDefinitionId === slotId,
  );

  // Current row to display in the summary table.
  const current =
    scope === "STANDARD"
      ? baseline
      : scope === "DATE"
        ? dateRow
        : scope === "SLOT"
          ? slotRow
          : dateSlotRow;

  // Re-sync form state when the resolved `current` row's *identity*
  // changes — i.e. the user switched scope and is now editing a
  // different CapacityConfig. Tracking identity (id) rather than
  // reference equality matters because TanStack Query returns a new
  // data wrapper on every refetch, which would make a `[current]`
  // effect clobber an in-flight keystroke back to the previously
  // saved value. After a successful save the id is unchanged, so we
  // intentionally do not re-sync — the user already typed the values
  // they wanted to save and they match the now-persisted row.
  // Sync disabled for debugging — confirm test passes without effect.
  // useEffect(() => {}, [current?.id]);
  // Sync form state from the resolved `current` row when the user has
  // not yet edited any field (form is "pristine"). After the first edit
  // the dirty ref flips to true and the effect bails out, so the user's
  // keystrokes are never clobbered by a late-arriving query refetch.
  // The scope-switch handler resets dirty to false so that switching from
  // STANDARD → DATE etc. picks up the new row's values.
  const currentId = current?.id ?? null;
  const lastSyncedIdRef = useRef<string | null>(null);
  const dirtyRef = useRef(false);
  useEffect(() => {
    if (dirtyRef.current) return;
    if (lastSyncedIdRef.current === currentId) return;
    lastSyncedIdRef.current = currentId;
    setCapacityOverride(
      current?.capacityOverride !== null &&
        current?.capacityOverride !== undefined
        ? String(current.capacityOverride)
        : "",
    );
    setOnlineHoldback(current?.onlineHoldback ?? 0);
    setTelefonHoldback(current?.telefonHoldback ?? 0);
    setVorOrtHoldback(current?.vorOrtHoldback ?? 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  const upsertMutation = useMutation({
    mutationFn: async (input: {
      date: string | null;
      slotDefinitionId: string | null;
      onlineHoldback: number;
      telefonHoldback: number;
      vorOrtHoldback: number;
      capacityOverride: number | null;
      slotCapacityOverride: number | null;
    }) =>
      gqlRequest<{ upsertCapacityConfig: CapacityConfig }>(
        UPSERT_CAPACITY_CONFIG_MUTATION,
        { input },
      ),
    onSuccess: () => {
      invalidateAvailabilityCaches(queryClient, "capacityConfigs");
    },
  });

  function handleSave() {
    // Parse the capacity override input: empty string → null (= use the
    // Restaurant.capacityDefault fallback), otherwise a non-negative
    // integer. Mirrors the slot-editor parse in this file (SlotEditor)
    // so the two surfaces stay consistent.
    const capacityParsed =
      capacityOverride.trim().length === 0
        ? null
        : Number.parseInt(capacityOverride, 10);
    const capacityValue =
      capacityParsed !== null && Number.isFinite(capacityParsed)
        ? Math.max(0, capacityParsed)
        : null;
    const input = {
      date: scope === "DATE" || scope === "DATE_SLOT" ? date || null : null,
      slotDefinitionId:
        scope === "SLOT" || scope === "DATE_SLOT" ? slotId || null : null,
      onlineHoldback: clampHoldback(onlineHoldback),
      telefonHoldback: clampHoldback(telefonHoldback),
      vorOrtHoldback: clampHoldback(vorOrtHoldback),
      capacityOverride: capacityValue,
      slotCapacityOverride: null,
    };
    upsertMutation.mutate(input);
  }

  const slots = slotsQuery.data?.slotDefinitions ?? [];
  const total = current?.capacityOverride ?? success?.capacityDefault ?? 0;
  const onlineHoldbackValue = current?.onlineHoldback ?? 0;
  const available = Math.max(0, total - onlineHoldbackValue);

  return (
    <section data-testid="section-capacity">
      <h2 className="font-heading text-xl mb-2">
        {t("availability.admin.sectionCapacity")}
      </h2>
      <p className="text-sm text-text/70 mb-3">
        {t("availability.admin.capacity.helper")}
      </p>

      <div
        className="flex flex-wrap rounded-md border border-divider overflow-hidden mb-3"
        role="radiogroup"
        aria-label={t("availability.admin.sectionCapacity")}
        data-testid="capacity-scope"
      >
        {(["STANDARD", "DATE", "SLOT", "DATE_SLOT"] as CapacityScope[]).map(
          (s) => {
            const isActive = scope === s;
            return (
              <button
                type="button"
                key={s}
                role="radio"
                aria-checked={isActive}
                onClick={() => {
                  // Switching scope picks a different CapacityConfig;
                  // allow the sync effect to overwrite the form with
                  // the new row's values.
                  dirtyRef.current = false;
                  lastSyncedIdRef.current = null;
                  setScope(s);
                }}
                className={
                  "px-3 py-2 text-sm font-medium border-r border-divider last:border-r-0 " +
                  (isActive
                    ? "bg-accent-700 text-bg"
                    : "bg-surface text-text hover:bg-accent/10")
                }
                data-testid={`capacity-scope-${s}`}
              >
                {t(SCOPE_LABEL_KEY[s])}
              </button>
            );
          },
        )}
      </div>

      {(scope === "DATE" || scope === "DATE_SLOT") && (
        <label className="block mb-3">
          <span className="block text-sm font-medium text-text/70 mb-1">
            {t("availability.admin.capacity.pickDate")}
          </span>
          <input
            type="date"
            value={date}
            onChange={(e) => {
              // Changing the date picks a different CapacityConfig (or
              // none); let the sync effect re-seed the form from the
              // new row.
              dirtyRef.current = false;
              lastSyncedIdRef.current = null;
              setDate(e.target.value);
            }}
            className="field w-full"
            data-testid="capacity-date"
          />
        </label>
      )}
      {(scope === "SLOT" || scope === "DATE_SLOT") && (
        <label className="block mb-3">
          <span className="block text-sm font-medium text-text/70 mb-1">
            {t("availability.admin.capacity.pickSlot")}
          </span>
          <select
            value={slotId}
            onChange={(e) => {
              dirtyRef.current = false;
              lastSyncedIdRef.current = null;
              setSlotId(e.target.value);
            }}
            className="field w-full"
            data-testid="capacity-slot"
          >
            <option value="">—</option>
            {slots.map((s) => (
              <option key={s.id} value={s.id}>
                {s.startTime}
              </option>
            ))}
          </select>
        </label>
      )}

      <div className="border border-divider rounded-md overflow-x-auto">
        <table
          className="w-full text-sm border-collapse"
          data-testid="capacity-table"
        >
          <caption className="text-left text-sm font-medium text-text/70 px-3 py-2 border-b border-divider">
            {t("availability.admin.capacity.helper")}
          </caption>
          <thead>
            <tr className="text-left text-text/70 bg-surface">
              <th scope="col" className="px-3 py-2 border-b border-divider">
                {t("availability.admin.capacity.columnTotal")}
              </th>
              <th scope="col" className="px-3 py-2 border-b border-divider">
                {t("availability.admin.capacity.columnOnline")}
              </th>
              <th scope="col" className="px-3 py-2 border-b border-divider">
                {t("availability.admin.capacity.columnPhone")}
              </th>
              <th scope="col" className="px-3 py-2 border-b border-divider">
                {t("availability.admin.capacity.columnWalkIn")}
              </th>
              <th scope="col" className="px-3 py-2 border-b border-divider">
                {t("availability.admin.capacity.columnAvailable")}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr data-testid="capacity-row">
              <td className="px-3 py-2 border-b border-divider tabular-nums">
                {total}
              </td>
              <td className="px-3 py-2 border-b border-divider tabular-nums">
                {current?.onlineHoldback ?? 0}
              </td>
              <td className="px-3 py-2 border-b border-divider tabular-nums">
                {current?.telefonHoldback ?? 0}
              </td>
              <td className="px-3 py-2 border-b border-divider tabular-nums">
                {current?.vorOrtHoldback ?? 0}
              </td>
              <td
                className="px-3 py-2 border-b border-divider tabular-nums"
                data-testid="capacity-available"
              >
                {available}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
        <label className="block">
          <span className="block text-sm font-medium text-text/70 mb-1">
            {t("availability.admin.capacity.columnTotal")}
          </span>
          <input
            type="number"
            min={0}
            max={10000}
            value={capacityOverride}
            onChange={(e) => {
              dirtyRef.current = true;
              setCapacityOverride(e.target.value);
            }}
            className="field w-full"
            data-testid="capacity-input-total"
          />
          <span className="block text-sm text-text/70 mt-1">
            {t("availability.admin.capacity.helperTotal", {
              default: success?.capacityDefault ?? 0,
            })}
          </span>
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-text/70 mb-1">
            {t("availability.admin.capacity.columnOnline")}
          </span>
          <input
            type="number"
            min={0}
            // Phase 4 follow-up #3: per-channel cap matches the server
            // zod `max(10)` in availability-admin.ts (HOLDBAX_MAX_PER_CHANNEL).
            // Defense in depth — the form refuses to send out-of-range
            // values; the server is still the source of truth.
            max={10}
            value={onlineHoldback}
            onChange={(e) => {
              dirtyRef.current = true;
              setOnlineHoldback(
                clampHoldback(Number.parseInt(e.target.value, 10)),
              );
            }}
            className="field w-full"
            data-testid="capacity-input-online"
          />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-text/70 mb-1">
            {t("availability.admin.capacity.columnPhone")}
          </span>
          <input
            type="number"
            min={0}
            max={10}
            value={telefonHoldback}
            onChange={(e) => {
              dirtyRef.current = true;
              setTelefonHoldback(
                clampHoldback(Number.parseInt(e.target.value, 10)),
              );
            }}
            className="field w-full"
            data-testid="capacity-input-phone"
          />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-text/70 mb-1">
            {t("availability.admin.capacity.columnWalkIn")}
          </span>
          <input
            type="number"
            min={0}
            max={10}
            value={vorOrtHoldback}
            onChange={(e) => {
              dirtyRef.current = true;
              setVorOrtHoldback(
                clampHoldback(Number.parseInt(e.target.value, 10)),
              );
            }}
            className="field w-full"
            data-testid="capacity-input-walkin"
          />
        </label>
      </div>

      <div className="flex justify-end mt-3">
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={upsertMutation.isPending}
          data-testid="capacity-save"
        >
          {t("availability.admin.capacity.save")}
        </button>
      </div>

      {upsertMutation.isSuccess && (
        <p
          className="text-sm text-text/70 mt-2"
          role="status"
          data-testid="capacity-success"
        >
          {t("availability.actions.saveSuccess")}
        </p>
      )}
      {upsertMutation.isError && (
        <p
          className="text-sm text-red-700 mt-2"
          role="alert"
          data-testid="capacity-error"
        >
          {t("availability.actions.saveFailure")}
        </p>
      )}
    </section>
  );
}

// ---------------- Section 3: BlockedPeriod ----------------

function BlockedPeriodsSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const blocksQuery = useQuery<{ blockedPeriods: BlockedPeriod[] }>({
    queryKey: ["availability-admin", "blockedPeriods"],
    queryFn: async () =>
      gqlRequest<{ blockedPeriods: BlockedPeriod[] }>(BLOCKED_PERIODS_QUERY),
  });

  const [editorOpen, setEditorOpen] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [allDay, setAllDay] = useState(true);
  const [fromTime, setFromTime] = useState("");
  const [toTime, setToTime] = useState("");
  const [reason, setReason] = useState("");
  const [armedDeleteId, setArmedDeleteId] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const blocks = useMemo(
    () =>
      (blocksQuery.data?.blockedPeriods ?? []).slice().sort((a, b) => {
        if (a.fromDate !== b.fromDate) {
          return a.fromDate.localeCompare(b.fromDate);
        }
        const aT = a.fromTime ?? "";
        const bT = b.fromTime ?? "";
        return aT.localeCompare(bT);
      }),
    [blocksQuery.data],
  );

  const createMutation = useMutation({
    mutationFn: async (vars: {
      input: {
        fromDate: string;
        toDate: string;
        fromTime: string | null;
        toTime: string | null;
        reason: string | null;
      };
      forceFlag: boolean;
    }) =>
      gqlRequest<{
        createBlockedPeriod: {
          blockedPeriod: BlockedPeriod;
          conflicts: unknown[];
          conflictingReservationCount: number;
          requiresForce: boolean;
        };
      }>(CREATE_BLOCKED_PERIOD_MUTATION, vars),
    onSuccess: () => {
      invalidateAvailabilityCaches(queryClient, "blockedPeriods");
      setEditorOpen(false);
      setFromDate("");
      setToDate("");
      setFromTime("");
      setToTime("");
      setReason("");
      setValidationError(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) =>
      gqlRequest<{ deleteBlockedPeriod: boolean }>(
        DELETE_BLOCKED_PERIOD_MUTATION,
        { id },
      ),
    onSuccess: () => {
      invalidateAvailabilityCaches(queryClient, "blockedPeriods");
      setArmedDeleteId(null);
    },
  });

  function validateAndSubmit() {
    setValidationError(null);
    if (!fromDate || !toDate) {
      setValidationError(t("availability.admin.blockedPeriod.fromDate"));
      return;
    }
    if (fromDate > toDate) {
      setValidationError(t("availability.admin.blockedPeriod.dateRangeError"));
      return;
    }
    if (!allDay) {
      if (!fromTime || !toTime) {
        setValidationError(t("availability.admin.blockedPeriod.rolloverError"));
        return;
      }
      if (fromTime >= toTime) {
        setValidationError(
          t("availability.admin.blockedPeriod.timeRangeError"),
        );
        return;
      }
    }
    createMutation.mutate({
      input: {
        fromDate,
        toDate,
        fromTime: allDay ? null : fromTime,
        toTime: allDay ? null : toTime,
        reason: reason.trim().length === 0 ? null : reason.trim(),
      },
      forceFlag: false,
    });
  }

  return (
    <section data-testid="section-blocked">
      <h2 className="font-heading text-xl mb-2">
        {t("availability.admin.sectionBlockedPeriods")}
      </h2>

      <div className="flex justify-end mb-3">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => setEditorOpen(!editorOpen)}
          data-testid="blocked-add"
        >
          {t("availability.admin.blockedPeriod.add")}
        </button>
      </div>

      {editorOpen && (
        <div className="card p-4 mb-4 space-y-3" data-testid="blocked-editor">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-sm font-medium text-text/70 mb-1">
                {t("availability.admin.blockedPeriod.fromDate")}
              </span>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="field w-full"
                data-testid="blocked-from-date"
              />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-text/70 mb-1">
                {t("availability.admin.blockedPeriod.toDate")}
              </span>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="field w-full"
                data-testid="blocked-to-date"
              />
            </label>
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              role="switch"
              checked={allDay}
              onChange={(e) => setAllDay(e.target.checked)}
              data-testid="blocked-all-day"
            />
            <span>{t("availability.admin.blockedPeriod.allDay")}</span>
          </label>

          {!allDay && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-sm font-medium text-text/70 mb-1">
                  {t("availability.admin.blockedPeriod.fromTime")}
                </span>
                <input
                  type="time"
                  value={fromTime}
                  onChange={(e) => setFromTime(e.target.value)}
                  className="field w-full"
                  data-testid="blocked-from-time"
                />
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-text/70 mb-1">
                  {t("availability.admin.blockedPeriod.toTime")}
                </span>
                <input
                  type="time"
                  value={toTime}
                  onChange={(e) => setToTime(e.target.value)}
                  className="field w-full"
                  data-testid="blocked-to-time"
                />
              </label>
            </div>
          )}

          <label className="block">
            <span className="block text-sm font-medium text-text/70 mb-1">
              {t("availability.admin.blockedPeriod.reason")}
            </span>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="field w-full"
              data-testid="blocked-reason"
            />
          </label>

          {validationError && (
            <p
              className="text-sm text-red-700"
              role="alert"
              data-testid="blocked-validation-error"
            >
              {validationError}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setEditorOpen(false)}
              data-testid="blocked-cancel"
            >
              {t("availability.admin.blockedPeriod.cancel")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={validateAndSubmit}
              disabled={createMutation.isPending}
              data-testid="blocked-save"
            >
              {t("availability.admin.blockedPeriod.save")}
            </button>
          </div>
        </div>
      )}

      {blocksQuery.isLoading && <p className="text-sm text-text/70">…</p>}

      {blocksQuery.data && blocks.length === 0 && (
        <p className="text-sm text-text/70" data-testid="blocked-empty">
          {t("availability.admin.blockedPeriod.empty")}
        </p>
      )}

      {blocks.length > 0 && (
        <ul className="card divide-y divide-divider" data-testid="blocked-list">
          {blocks.map((b) => {
            const range = b.fromTime
              ? `${b.fromDate} ${b.fromTime}–${b.toTime ?? ""}`
              : b.fromDate === b.toDate
                ? b.fromDate
                : `${b.fromDate} – ${b.toDate}`;
            return (
              <li
                key={b.id}
                className="px-4 py-3 flex flex-wrap items-center justify-between gap-2"
                data-testid={`blocked-row-${b.id}`}
              >
                <div className="flex flex-col">
                  <span className="font-medium">{range}</span>
                  {b.reason && (
                    <span className="text-sm text-text/70">{b.reason}</span>
                  )}
                </div>
                {armedDeleteId !== b.id && (
                  <button
                    type="button"
                    className="p-1 text-text/70 hover:text-red-700"
                    onClick={() => setArmedDeleteId(b.id)}
                    aria-label={t(
                      "availability.admin.blockedPeriod.deleteConfirm",
                    )}
                    data-testid={`blocked-row-${b.id}-delete`}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
                {armedDeleteId === b.id && (
                  <div
                    className="flex flex-wrap items-center gap-2"
                    role="group"
                    data-testid={`blocked-row-${b.id}-armed`}
                  >
                    <span className="text-sm">
                      {t("availability.admin.blockedPeriod.deleteArmed", {
                        range,
                      })}
                    </span>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setArmedDeleteId(null)}
                      data-testid={`blocked-row-${b.id}-cancel`}
                    >
                      {t("availability.admin.blockedPeriod.cancel")}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => deleteMutation.mutate(b.id)}
                      data-testid={`blocked-row-${b.id}-confirm`}
                    >
                      {t("availability.admin.blockedPeriod.deleteConfirm")}
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// ---------------- Section 4: Booking rules + confirmationMode ----------------

function BookingRulesSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const restaurantQuery = useQuery<{ restaurant: RestaurantPolicy }>({
    queryKey: ["restaurant", "policy"],
    queryFn: async () =>
      gqlRequest<{ restaurant: RestaurantPolicy }>(RESTAURANT_QUERY),
  });

  const r = restaurantQuery.data?.restaurant;

  const [maxAdvanceDays, setMaxAdvanceDays] = useState<number>(
    r?.maxAdvanceDays ?? 60,
  );
  const [minAdvanceMinutes, setMinAdvanceMinutes] = useState<number>(
    r?.minAdvanceMinutes ?? 120,
  );
  const [sameDayCutoff, setSameDayCutoff] = useState<string>(
    r?.sameDayCutoffTime ?? "21:00",
  );
  const [minPartySize, setMinPartySize] = useState<number>(
    r?.minPartySize ?? 1,
  );
  const [maxPartySize, setMaxPartySize] = useState<number>(
    r?.maxPartySize ?? 20,
  );
  const [confirmationMode, setConfirmationMode] = useState<ConfirmationMode>(
    r?.confirmationMode ?? "AUTO",
  );

  // Re-sync local form state when the restaurant query resolves.
  useMemo(() => {
    if (r) {
      setMaxAdvanceDays(r.maxAdvanceDays);
      setMinAdvanceMinutes(r.minAdvanceMinutes);
      setSameDayCutoff(r.sameDayCutoffTime);
      setMinPartySize(r.minPartySize);
      setMaxPartySize(r.maxPartySize);
      setConfirmationMode(r.confirmationMode);
    }
  }, [r]);

  const policyMutation = useMutation({
    mutationFn: async () => {
      // Per D-16: other policy fields go through updateRestaurant;
      // confirmationMode always uses the dedicated mutation.
      await gqlRequest<{ updateRestaurant: { id: string } }>(
        UPDATE_RESTAURANT_MUTATION,
        {
          input: {
            minPartySize,
            maxPartySize,
            maxAdvanceDays,
            minAdvanceMinutes,
            sameDayCutoffTime: sameDayCutoff,
          },
        },
      );
      await gqlRequest<{ updateConfirmationMode: boolean }>(
        UPDATE_CONFIRMATION_MODE_MUTATION,
        { mode: confirmationMode },
      );
      return true;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["restaurant"] });
      void queryClient.invalidateQueries({
        queryKey: ["availability-admin"],
      });
      void queryClient.invalidateQueries({ queryKey: ["availability"] });
      void queryClient.invalidateQueries({ queryKey: ["slots"] });
      // Reset the success flag timer.
      setSuccessStamp(Date.now());
    },
  });

  const [successStamp, setSuccessStamp] = useState<number | null>(null);
  const successVisible =
    policyMutation.isSuccess &&
    successStamp !== null &&
    Date.now() - successStamp < 8000;

  return (
    <section data-testid="section-rules">
      <h2 className="font-heading text-xl mb-2">
        {t("availability.admin.sectionBookingRules")}
      </h2>

      <div className="card p-4 space-y-3">
        <label className="block">
          <span className="block text-sm font-medium text-text/70 mb-1">
            {t("availability.admin.rules.maxAdvanceDays")}
          </span>
          <input
            type="number"
            min={1}
            max={365}
            value={maxAdvanceDays}
            onChange={(e) =>
              setMaxAdvanceDays(Number.parseInt(e.target.value, 10) || 0)
            }
            className="field w-full"
            data-testid="rules-max-advance-days"
          />
          <span className="block text-sm text-text/70 mt-1">
            {t("availability.admin.rules.advancedWindowHint", {
              days: maxAdvanceDays,
            })}
          </span>
        </label>

        <label className="block">
          <span className="block text-sm font-medium text-text/70 mb-1">
            {t("availability.admin.rules.minAdvanceMinutes")}
          </span>
          <input
            type="number"
            min={0}
            value={minAdvanceMinutes}
            onChange={(e) =>
              setMinAdvanceMinutes(Number.parseInt(e.target.value, 10) || 0)
            }
            className="field w-full"
            data-testid="rules-min-advance-minutes"
          />
        </label>

        <label className="block">
          <span className="block text-sm font-medium text-text/70 mb-1">
            {t("availability.admin.rules.sameDayCutoff")}
          </span>
          <input
            type="time"
            value={sameDayCutoff}
            onChange={(e) => setSameDayCutoff(e.target.value)}
            className="field w-full"
            data-testid="rules-same-day-cutoff"
          />
          <span className="block text-sm text-text/70 mt-1">
            {t("availability.admin.rules.sameDayCutoffHint")}
          </span>
        </label>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-sm font-medium text-text/70 mb-1">
              {t("availability.admin.rules.minPartySize")}
            </span>
            <input
              type="number"
              min={1}
              value={minPartySize}
              onChange={(e) =>
                setMinPartySize(Number.parseInt(e.target.value, 10) || 0)
              }
              className="field w-full"
              data-testid="rules-min-party-size"
            />
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-text/70 mb-1">
              {t("availability.admin.rules.maxPartySize")}
            </span>
            <input
              type="number"
              min={1}
              value={maxPartySize}
              onChange={(e) =>
                setMaxPartySize(Number.parseInt(e.target.value, 10) || 0)
              }
              className="field w-full"
              data-testid="rules-max-party-size"
            />
          </label>
        </div>

        <fieldset className="space-y-2">
          <legend className="block text-sm font-medium text-text/70 mb-1">
            {t("availability.admin.rules.confirmationModeLabel")}
          </legend>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="confirmationMode"
              checked={confirmationMode === "AUTO"}
              onChange={() => setConfirmationMode("AUTO")}
              data-testid="rules-confirmation-auto"
            />
            <span>{t("availability.admin.rules.confirmationModeAuto")}</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="confirmationMode"
              checked={confirmationMode === "MANUAL"}
              onChange={() => setConfirmationMode("MANUAL")}
              data-testid="rules-confirmation-manual"
            />
            <span>{t("availability.admin.rules.confirmationModeManual")}</span>
          </label>
        </fieldset>

        <div className="flex justify-end">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => policyMutation.mutate()}
            disabled={policyMutation.isPending}
            data-testid="rules-save"
          >
            {t("availability.admin.rules.save")}
          </button>
        </div>

        {successVisible && (
          <p
            className="text-sm text-text/70"
            role="status"
            data-testid="rules-success"
          >
            {t("availability.actions.saveSuccess")}
          </p>
        )}
        {policyMutation.isError && (
          <p
            className="text-sm text-red-700"
            role="alert"
            data-testid="rules-error"
          >
            {t("availability.actions.saveFailure")}
          </p>
        )}
      </div>
    </section>
  );
}

export { invalidateAvailabilityCaches };
